import { useState } from 'react';
import type { Lang } from '@fakerpg/core';
import { BookOpen, Hourglass, LogIn, LogOut, RefreshCw, Sparkles, WifiOff } from 'lucide-react';
import { useSnapshot, useStore } from '../game/context.js';
import { LOGIN_URL } from '../game/api.js';
import type { Phase } from '../game/store.js';
import { loadLang, translate, type MessageKey } from '../i18n/index.js';
import { ItemArt, MonsterArt } from '../assets/art.js';

/**
 * Everything before the game shell (T3.7): the Discord login screen and the
 * states where no account is shown — loading, offline, no bot account on a
 * read-only server, server error. Online only: there is no guest mode.
 */
export function Gate({ phase }: { phase: Exclude<Phase, 'ready'> }) {
  const store = useStore();
  const { notice, prefs } = useSnapshot();
  const [lang, setLang] = useState<Lang>(prefs.lang);
  const t = (key: MessageKey) => translate(lang, key);
  const pickLang = (value: Lang) =>
    void loadLang(value).then(() => {
      setLang(value);
      store.setPrefs({ lang: value });
    });

  return (
    <main className="welcome" aria-busy={phase === 'loading'}>
      <div className="backdrop" aria-hidden="true" />
      <div className="welcome__art" aria-hidden="true">
        <span className="welcome__card welcome__card--1">
          <ItemArt type="characters" id={27} size={110} />
        </span>
        <span className="welcome__card welcome__card--2">
          <ItemArt type="weapons" id={31} size={110} />
        </span>
        <span className="welcome__card welcome__card--3">
          <ItemArt type="pets" id={22} size={110} />
        </span>
        <span className="welcome__monster">
          <MonsterArt seed="welcome-tofu" element="Agility" size={120} />
        </span>
      </div>
      <div className="welcome__panel">
        <div className="brand-mark brand-mark--big" aria-hidden="true">
          F
        </div>
        <h1 className="welcome__title">{t('app.title')}</h1>
        <p className="welcome__tagline">{t('app.tagline')}</p>

        {phase === 'loading' && <p className="muted center">{t('gate.loading')}</p>}

        {phase === 'signedOut' && (
          <>
            <ul className="welcome__pitch">
              <li>
                <Sparkles size={16} aria-hidden="true" /> {t('gate.pitch1')}
              </li>
              <li>
                <BookOpen size={16} aria-hidden="true" /> {t('gate.pitch2')}
              </li>
              <li>
                <Hourglass size={16} aria-hidden="true" /> {t('gate.pitch3')}
              </li>
            </ul>
            {notice && (
              <p className={`notice ${notice === 'banned' || notice === 'failed' ? 'notice--error' : ''}`} role="alert">
                {t(`gate.notice.${notice}`)}
              </p>
            )}
            <div className="welcome__form">
              <a className="btn btn--primary btn--lg btn--block" href={LOGIN_URL}>
                <LogIn size={18} aria-hidden="true" />
                <span className="btn__label">{t('gate.login')}</span>
              </a>
              <p className="muted small">{t('gate.loginHelp')}</p>
            </div>
          </>
        )}

        {phase === 'offline' && (
          <div className="welcome__form">
            <p className="notice notice--offline" role="status">
              <WifiOff size={16} aria-hidden="true" />
              <span>{t('gate.offline')}</span>
            </p>
            <button type="button" className="btn btn--secondary btn--block" onClick={() => void store.load()}>
              <RefreshCw size={16} aria-hidden="true" />
              <span className="btn__label">{t('gate.retry')}</span>
            </button>
          </div>
        )}

        {phase === 'error' && (
          <div className="welcome__form">
            <p className="notice notice--error" role="alert">
              {t('gate.error')}
            </p>
            <button type="button" className="btn btn--secondary btn--block" onClick={() => void store.load()}>
              <RefreshCw size={16} aria-hidden="true" />
              <span className="btn__label">{t('gate.retry')}</span>
            </button>
          </div>
        )}

        {phase === 'noAccount' && (
          <div className="welcome__form">
            <p className="notice" role="status">
              {t('gate.noAccount')}
            </p>
            <button type="button" className="btn btn--secondary btn--block" onClick={() => void store.logout()}>
              <LogOut size={16} aria-hidden="true" />
              <span className="btn__label">{t('settings.logout')}</span>
            </button>
          </div>
        )}

        <div className="langpick" role="radiogroup" aria-label={t('gate.language')}>
          {(
            [
              ['fr', 'Français'],
              ['en', 'English'],
            ] as const
          ).map(([value, label]) => (
            <button key={value} type="button" role="radio" aria-checked={lang === value} className={`langpick__item ${lang === value ? 'is-active' : ''}`} onClick={() => pickLang(value)}>
              {label}
            </button>
          ))}
        </div>
      </div>
    </main>
  );
}
